import { EntityNotResolved, EntityResolved, User } from '@api';
import { dateToUTC } from '@helpers';
import { RowSelectionState } from '@tanstack/react-table';
import {
  ONBOARDING_STATE,
  useRemoteStorage,
} from '@terminal/hooks/use-remote-storage';
import { FormattedResolvedEntityV3Data } from '@terminal/stores/use-watchlist/entityResolver';
import { useWatchlist } from '@terminal/stores/use-watchlist/useWatchlist';
import { create } from '@terminal/utils/store/createStore';
import { isToday } from 'date-fns';

import { shouldOnBoardingBeActive } from '../utils/utils';

const prodReleaseStringDate = '2025-07-23T00:00:00Z';
const SUGGESTED_WATCHLIST_ID = 'suggestedWatchlistId';
const SUGGESTED_WATCHLIST_CREATION_DATE = 'suggestedWatchlistCreationDate';

type OnboardingStatus = 'completed' | 'not-started';
type OnboardingStep =
  | 'create-watchlist'
  | 'name-watchlist'
  | 'suggested-instruments'
  | 'follow-themes'
  | 'follow-geographies';

type FollowThemesEntryStep = 'name-watchlist' | 'suggested-instruments';

type EntitiesListUpdate =
  | EntityResolved[]
  | ((prev: EntityResolved[]) => EntityResolved[]);

const resolveEntitiesListUpdate = (
  update: EntitiesListUpdate,
  prev: EntityResolved[]
): EntityResolved[] => (typeof update === 'function' ? update(prev) : update);

type OnboardingStore = {
  status: OnboardingStatus;
  activeStep: OnboardingStep;
  followThemesEntryStep: FollowThemesEntryStep | null;
  drawerOpen: boolean;
  watchlistName?: string;
  suggestedWatchlistTags: string[];
  isNewUser: boolean;
  entitiesList: EntityResolved[];
  unMappedEntitiesList: EntityNotResolved[];
  allDuplicateTickers: string[];
  rowSelection: RowSelectionState;
  resolvedEntityData: FormattedResolvedEntityV3Data;
  manualEntityTags: string[];
  initStore: (user?: User) => void;
  setDrawerOpen: (open: boolean) => void;
  setActiveStep: (step: OnboardingStep) => void;
  getShowOnboarding: () => boolean;
  setEntitiesList: (update: EntitiesListUpdate) => void;
  setUnMappedEntitiesList: (unMappedEntitiesList: EntityNotResolved[]) => void;
  setRowSelection: (rowSelection: RowSelectionState) => void;
  setStatus: (status: OnboardingStatus) => void;
  setResolvedEntityData: (data?: FormattedResolvedEntityV3Data) => void;
  setAllDuplicateTickers: (allDuplicateTickers: string[]) => void;
  addManualEntityTags: (tags: string[]) => void;
  removeManualEntityTag: (tag: string) => void;
  clearStore: () => void;
  hasSuggestedWatchlistId: () => boolean;
  getIsOnboardingFinishedToday: () => boolean;
  setWatchlistName: (watchlistName: string) => void;
  setSuggestedWatchlistTags: (tags: string[]) => void;
};

const addManualTags = (existing: string[], tags: string[]) => {
  const next = new Set(existing);
  for (const tag of tags) {
    next.add(tag);
  }
  return [...next];
};

export const useOnboardingStore = create<OnboardingStore>((set, get) => ({
  initStore: user => {
    const { items } = useRemoteStorage.getState();

    if (!user) {
      return;
    }

    const onboardingStatus = items?.['ONBOARDING_STATE'] ?? 'not-started';

    const isNewUser =
      dateToUTC(new Date(user.created_at)) >=
      dateToUTC(new Date(prodReleaseStringDate));

    set({ status: onboardingStatus as OnboardingStatus, isNewUser });
  },
  resolvedEntityData: {
    tags: [],
    duplicates: [],
    entities: [],
  },
  // Combined state for CSV search duplicates and table duplicates
  allDuplicateTickers: [],
  watchlistName: undefined,
  suggestedWatchlistTags: [],
  status: 'not-started',
  activeStep: 'create-watchlist',
  followThemesEntryStep: null,
  entitiesList: [],
  unMappedEntitiesList: [],
  rowSelection: {},
  manualEntityTags: [],
  isNewUser: false,
  drawerOpen: false,
  getShowOnboarding: () => {
    const { isNewUser, status } = get();
    return shouldOnBoardingBeActive({
      isNewUser,
      isCompleted: status === 'completed',
    });
  },
  setDrawerOpen: open => set({ drawerOpen: open }),
  setActiveStep: step =>
    set(state => {
      if (
        step === 'follow-themes' &&
        (state.activeStep === 'name-watchlist' ||
          state.activeStep === 'suggested-instruments')
      ) {
        return { activeStep: step, followThemesEntryStep: state.activeStep };
      }

      return { activeStep: step };
    }),
  setEntitiesList: update =>
    set(state => ({
      entitiesList: resolveEntitiesListUpdate(update, state.entitiesList),
    })),
  setUnMappedEntitiesList: unMappedEntitiesList =>
    set({ unMappedEntitiesList }),
  setRowSelection: rowSelection => set({ rowSelection }),
  setWatchlistName: watchlistName => set({ watchlistName }),
  setStatus: status => {
    const { storeItems } = useRemoteStorage.getState();

    storeItems({
      [ONBOARDING_STATE]: status,
    });
    set({ status });
  },
  setResolvedEntityData: data => {
    const resolvedEntityData = data ?? {
      tags: [],
      duplicates: [],
      entities: [],
    };
    set({ resolvedEntityData });
  },
  setAllDuplicateTickers: allDuplicateTickers => set({ allDuplicateTickers }),
  addManualEntityTags: tags =>
    set(state => ({
      manualEntityTags: addManualTags(state.manualEntityTags, tags),
    })),
  removeManualEntityTag: tag =>
    set(state => ({
      manualEntityTags: state.manualEntityTags.filter(t => t !== tag),
    })),
  clearStore: () => {
    const { setResolvedEntityData } = get();
    setResolvedEntityData();

    set({
      entitiesList: [],
      unMappedEntitiesList: [],
      manualEntityTags: [],
      resolvedEntityData: {
        tags: [],
        duplicates: [],
        entities: [],
      },
    });
  },
  hasSuggestedWatchlistId: () => {
    const watchlists = useWatchlist.getState().lists;
    const watchlistId = localStorage.getItem(SUGGESTED_WATCHLIST_ID);

    if (watchlistId) {
      return watchlists.some(watchlist => watchlist.id === watchlistId);
    }

    return false;
  },
  getIsOnboardingFinishedToday: () => {
    const createdDate = localStorage.getItem(SUGGESTED_WATCHLIST_CREATION_DATE);
    return !!createdDate && isToday(new Date(createdDate));
  },
  setSuggestedWatchlistTags: tags => set({ suggestedWatchlistTags: tags }),
}));
